/**
 * InvitationSuccessDialog Component
 * 
 * Dialog shown after successfully sending an invitation.
 */

import { useState } from "react";
import { Stack, Alert, Typography, Box, TextField, Button, useTheme, useMediaQuery } from "@mui/material";
import CopyFeedbackTooltip from "../../../components/CopyFeedbackTooltip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StandardDialog from "../../../components/StandardDialog";
import { copyToClipboard } from "../../../utils/transactionUtils";

export interface InvitationSuccessDialogProps {
  open: boolean;
  onClose: () => void;
  emailSent: boolean;
  emailError?: { type: string; message: string };
  createdInvitationLink: string | null;
}

export default function InvitationSuccessDialog({
  open,
  onClose,
  emailSent,
  emailError,
  createdInvitationLink,
}: InvitationSuccessDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  const handleClose = (): void => {
    onClose();
  };

  const handleCopyLink = async (): Promise<void> => {
    if (!createdInvitationLink) return;
    const ok = await copyToClipboard(createdInvitationLink);
    if (ok) {
      setCopySnackbarOpen(true);
      setTimeout(() => setCopySnackbarOpen(false), 2000);
    }
  };

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title="Invitation Sent"
      confirmText="Done"
      onConfirm={handleClose}
      showCancel={false}
      confirmColor="primary"
      maxWidth="sm"
    >
      <Stack spacing={3}>
        {/* Email Status */}
        {emailSent ? (
          <Alert severity="success">
            <Typography variant="body2">
              An invitation email has been sent to the recipient. They will receive a link to join the workspace.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info">
            <Typography variant="body2" sx={{ mb: 1 }}>
              Invitation created successfully. Please share the invitation link below with the user manually.
            </Typography>
            {emailError?.type === "not_configured" && (
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                Email sending is not configured. To enable email sending, configure your backend email settings.
              </Typography>
            )}
          </Alert>
        )}

        {/* Invitation Link Section */}
        <Box>
          <Typography variant="body2" fontWeight="medium" sx={{ mb: 1.5 }}>
            Invitation Link
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              value={createdInvitationLink || ""}
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
              }}
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "monospace",
                  fontSize: "0.8125rem",
                  py: 1.25,
                },
                "& .MuiOutlinedInput-root": {
                  bgcolor: "background.default",
                },
              }}
            />
            <CopyFeedbackTooltip
              copyLabel="Copy link"
              feedbackKey="invite-link"
              lastCopiedKey={copySnackbarOpen ? "invite-link" : null}
              isMobile={isMobile}
            >
              <Button
                variant="outlined"
                onClick={handleCopyLink}
                sx={{ minWidth: "auto", px: 2 }}
              >
                <ContentCopyIcon fontSize="small" />
              </Button>
            </CopyFeedbackTooltip>
          </Box>
          <Typography variant="caption" color="text.secondary">
            This invitation will expire in 7 days
          </Typography>
        </Box>
      </Stack>
    </StandardDialog>
  );
}
